<template>
  <ItemRow
    v-bind="$attrs"
    class="ItemRow"
    :selected="itemData.selected"
    v-on="$listeners"
    @click.native="handleItemClick"
    @keypress.prevent.native="handleItemKeypress"
  >
    <ItemWithLabel
      :class="$options.ItemWithLabelClassName"
      :column="columns[0]"
    >
      <div class="tooltip-icon__wrapper">
        <TooltipIcon
          ariaLabel="application status"
          :class="[
            'status-icon',
            appStatusTooltipData.strokeColorClass,
          ]"
          :hasIndicator="false"
          :icon="appStatusTooltipData.iconName"
          :isMarkdown="false"
          :linkHref="appStatusTooltipData.linkHref"
          :linkText="appStatusTooltipData.linkText"
          orientation="right"
          :text="appStatusTooltipData.tooltipText"
        />
      </div>
    </ItemWithLabel>

    <ItemWithLabel
      :class="[
        $options.ItemWithLabelClassName,
        'logo-image__container',
      ]"
      :column="columns[1]"
    >
      <img
        v-if="showLogoImage"
        class="logo-image"
        :src="itemData.logo.url"
      >
      <div
        v-else
        class="color-block"
        :style="{ backgroundColor: itemData.logo.color }"
      >
        <p class="icon-letter">
          {{ itemData.displayLabel.charAt(0) }}
        </p>
      </div>
    </ItemWithLabel>

    <ItemWithLabel
      :class="$options.ItemWithLabelClassName"
      :column="columns[2]"
      :title="itemData.displayLabel"
    >
      <ParagraphBody
        bodySize="large"
        class="display-label"
        :data-test-id="$testId('displayLabelParagraphBody')"
        :hasMargin="false"
      >
        {{ itemData.displayLabel }}
      </ParagraphBody>
    </ItemWithLabel>

    <ItemWithLabel
      :class="$options.ItemWithLabelClassName"
      :column="columns[3]"
      :title="`${itemData.sso.hidden ? 'Hidden' : 'Visible'} in User Portal`"
    >
      <ParagraphBody
        bodySize="large"
        class="display-label display-label--visibility"
        :data-test-id="$testId('visibilityParagraphBody')"
        :hasMargin="false"
      >
        <Icon
          v-if="itemData.sso.hidden"
          class="visibility-icon"
          :data-test-id="$testId('visibilityIcon')"
          icon="viewableOff"
        />
        {{ itemData.sso.hidden ? 'No' : 'Yes' }}
      </ParagraphBody>
    </ItemWithLabel>

    <ItemWithLabel
      :class="$options.ItemWithLabelClassName"
      :column="columns[4]"
    >
      <ApplicationFeatures
        :item="itemData"
        showNewComponents
      />
    </ItemWithLabel>

    <ItemWithLabel
      :class="$options.ItemWithLabelClassName"
      :column="columns[5]"
    >
      <div
        v-if="showExpirationDate"
        class="expiration-date__container"
      >
        <Icon
          v-if="certExpired || certAboutToExpire"
          :class="[
            appStatusTooltipData.strokeColorClass,
            'margin-right'
          ]"
          :data-test-id="$testId('certExpiredIcon')"
          :icon="$options.errorCircleIconName"
        />
        <ParagraphBody
          bodySize="large"
          :class="{ 'red-error__text': certExpired }"
          :data-test-id="$testId('idpCertExpirationText')"
          :hasMargin="false"
        >
          {{ certExpirationText }}
        </ParagraphBody>
      </div>
    </ItemWithLabel>

    <Icon
      class="chevron-right__icon"
      icon="chevronRight"
      :scale="1"
    />
  </ItemRow>
</template>

<script>
import {
  Icon,
  ItemRow,
  ItemWithLabel,
  ParagraphBody,
  TooltipIcon,
} from '@jumpcloud/ui-components';

import { ApplicationStatusTooltipTexts, EOL, EOS } from 'client/util/Constants/ApplicationConstants';

import ApplicationFeatures from 'client/components/ItemTable/BodyComponents/ApplicationFeatures.vue';
import DateUtil from 'client/util/DateUtil';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';

// icon names
const checkmarkCircleIconName = 'checkmarkCircle';
const errorCircleIconName = 'error';
const pauseCircleIconName = 'pauseCircle';

// color classes (stroke colors) for icons
const cautionYellowClassName = 'caution-yellow';
const errorRedClassName = 'error-red';
const pausedGreyClassName = 'inactive-grey';
const successGreenClassName = 'success-green';

const learnMoreText = 'Learn more.';
const ItemWithLabelClassName = 'ItemWithLabel';

// keypress event keycodes
const ENTER_KEY = 13;
const SPACE_KEY = 32;

export default {
  name: 'ApplicationItemListRow',

  components: {
    ApplicationFeatures,
    Icon,
    ItemRow,
    ItemWithLabel,
    ParagraphBody,
    TooltipIcon,
  },

  props: {
    columns: {
      default: () => ([]),
      type: Array,
    },

    itemData: {
      required: true,
      type: Object,
    },
  },

  errorCircleIconName,
  ItemWithLabelClassName,
  learnMoreText,

  computed: {
    appStatusTooltipData() {
      // an object of key: string pairs to standardize the strings used for
      // the status "state" (prevent spelling errors, more digestible code)
      const AppStatusEnum = {
        BOOKMARK_ACTIVE: 'BOOKMARK_ACTIVE',
        BOOKMARK_DEACTIVATED: 'BOOKMARK_DEACTIVATED',
        EOS_CERT_EXPIRED: 'EOS_CERT_EXPIRED',
        EOS_CERT_OK: 'EOS_CERT_OK',
        EOL: 'EOL',
        CERT_EXPIRED: 'CERT_EXPIRED',
        CERT_ABOUT_TO_EXPIRE: 'CERT_ABOUT_TO_EXPIRE',
        ACTIVE: 'ACTIVE',
        DEACTIVATED: 'DEACTIVATED',
      };

      // object to hold the data/props for the TooltipIcon
      // based on the state of the app item
      const IconStatusData = {
        [AppStatusEnum.BOOKMARK_ACTIVE]: {
          iconName: checkmarkCircleIconName,
          strokeColorClass: successGreenClassName,
          tooltipText: ApplicationStatusTooltipTexts.bookmarkActive,
        },
        [AppStatusEnum.BOOKMARK_DEACTIVATED]: {
          iconName: pauseCircleIconName,
          strokeColorClass: pausedGreyClassName,
          tooltipText: ApplicationStatusTooltipTexts.bookmarkDeactivated,
        },

        // even if the status of the app is EOS, but its cert is expired,
        // we want to display the "cert expired" text
        [AppStatusEnum.EOS_CERT_EXPIRED]: {
          iconName: errorCircleIconName,
          strokeColorClass: errorRedClassName,
          tooltipText: ApplicationStatusTooltipTexts.idpCertExpired,
        },

        // if the status is EOS, and its cert is not expired,
        // we then want to show the EOS tooltip text and the
        // link section on the Tooltip
        [AppStatusEnum.EOS_CERT_OK]: {
          iconName: errorCircleIconName,
          strokeColorClass: cautionYellowClassName,
          tooltipText: ApplicationStatusTooltipTexts.EOS,
          linkHref: KbArticleUrls.appEOLorEOS,
          linkText: this.$options.learnMoreText,
        },
        [AppStatusEnum.EOL]: {
          iconName: errorCircleIconName,
          strokeColorClass: errorRedClassName,
          tooltipText: ApplicationStatusTooltipTexts.EOL,
          linkHref: KbArticleUrls.appEOLorEOS,
          linkText: this.$options.learnMoreText,
        },
        [AppStatusEnum.CERT_EXPIRED]: {
          iconName: errorCircleIconName,
          strokeColorClass: errorRedClassName,
          tooltipText: ApplicationStatusTooltipTexts.idpCertExpired,
        },
        [AppStatusEnum.CERT_ABOUT_TO_EXPIRE]: {
          iconName: errorCircleIconName,
          strokeColorClass: cautionYellowClassName,
          tooltipText: ApplicationStatusTooltipTexts.idpCertExpiresSoon,
        },
        [AppStatusEnum.ACTIVE]: {
          iconName: checkmarkCircleIconName,
          strokeColorClass: successGreenClassName,
          tooltipText: ApplicationStatusTooltipTexts.active,
        },
        [AppStatusEnum.DEACTIVATED]: {
          iconName: pauseCircleIconName,
          strokeColorClass: pausedGreyClassName,
          tooltipText: ApplicationStatusTooltipTexts.deactivated,
        },
      };

      const item = this.itemData;
      const { status } = item;
      const isActive = item.active || item.sso.active;
      const isBookmark = item.name === 'bookmark' || item.name === '1Password';

      let appStatus;

      if (isBookmark) {
        appStatus = isActive ? AppStatusEnum.BOOKMARK_ACTIVE : AppStatusEnum.BOOKMARK_DEACTIVATED;
      } else if (status === EOS || status === EOL) {
        if (status === EOS) {
          appStatus = this.certExpired ? AppStatusEnum.EOS_CERT_EXPIRED : AppStatusEnum.EOS_CERT_OK;
        } else if (status === EOL) {
          appStatus = AppStatusEnum.EOL;
        }
      } else if (this.certExpired) {
        appStatus = AppStatusEnum.CERT_EXPIRED;
      } else if (this.certAboutToExpire) {
        appStatus = AppStatusEnum.CERT_ABOUT_TO_EXPIRE;
      } else if (isActive) {
        appStatus = AppStatusEnum.ACTIVE;
      } else {
        appStatus = AppStatusEnum.DEACTIVATED;
      }

      const appStatusTooltipData = IconStatusData[appStatus];

      return appStatusTooltipData;
    },

    certAboutToExpire() {
      const { idpCertExpirationAt } = this.itemData.sso;

      const certAboutToExpire = !this.certExpired
        && DateUtil.daysUntil(idpCertExpirationAt) <= 61;

      return this.certExpirationDateExists && certAboutToExpire;
    },

    certExpirationDateExists() {
      return this.itemData.sso.idpCertExpirationAt != null;
    },

    certExpirationText() {
      return this.certExpired
        ? 'Expired'
        : `Expires ${DateUtil.formatDate(this.itemData.sso?.idpCertExpirationAt)}`;
    },

    certExpired() {
      const { certExpired, idpCertExpirationAt } = this.itemData.sso;

      return (
        certExpired
        || (this.certExpirationDateExists && DateUtil.hasExpired(idpCertExpirationAt))
      );
    },

    showExpirationDate() {
      return !!this.itemData.sso?.idpCertExpirationAt;
    },

    showLogoImage() {
      return !!this.itemData.logo?.url?.length;
    },
  },

  methods: {
    executeEvent(e) {
      // ignore checkbox click
      if (e.target?.type === 'checkbox') {
        e.stopPropagation();
      } else {
        // Because the tooltip is inside each item and it overflows the item,
        // the click area for the item also includes the tooltip text popout.
        // We didn't want clicks on the popout text to do anything, so a check
        // for the target's classlist did the trick

        // e.target.classList -> DOMTokenList Object
        // need to cast it to an array to use `Array.includes`
        const targetClassList = Array.from(e.target?.classList || []);

        if (targetClassList.includes(this.$options.ItemWithLabelClassName)) {
          this.redirectToAppDetailsAside();
        }
      }
    },

    handleItemClick(e) {
      this.executeEvent(e);
    },

    handleItemKeypress(e) {
      // space or enter key
      if ([ENTER_KEY, SPACE_KEY].includes(e.keyCode)) {
        this.executeEvent(e);
      }
    },

    redirectToAppDetailsAside() {
      this.$router.push(`/sso/${this.itemData.id}/details`);
    },
  },
};
</script>

<style scoped>

@value (
  jcError,
  jcGrey200,
  jcGrey300,
  jcGrey400,
  jcSuccess,
  jcWarning
) from '~css/brand-colors.css';

.color-block {
  border-radius: 30px;
  height: 45px;
  position: relative;
  width: 45px;
}

.chevron-right__icon {
  bottom: 0;
  color: var(--jcGrey300);
  margin: auto;
  position: absolute !important;
  right: 2%;
  top: 0;
}

.display-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.display-label--visibility {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
}

.visibility-icon {
  margin-right: var(--jc-spacer-small);
}

.expiration-date__container {
  align-items: center;
  display: flex;
}

.icon-letter {
  color: white;
  font-size: 30px;
  font-weight: 400;
  left: 50%;
  position: absolute;
  text-transform: capitalize;
  top: 50%;
  transform: translate(-50%, -50%);
}

.icon-letter_container {
  align-items: center;
  display: flex;
  height: 100%;
}

.ItemRow {
  cursor: pointer;
  height: 75px;
  position: relative;
  width: 100%;
}

.ItemWithLabel {
  align-items: center;
  display: flex;
  overflow: hidden;
}

.logo-image {
  height: 60px;
  max-width: 90%;
  object-fit: contain;
}

.logo-image__container {
  height: 75px;
  padding-left: 0;
}

.status-icon {
  bottom: 1px !important;
  cursor: default;

  --tooltip-max-width: 250px;
}

.tooltip-icon__wrapper {
  position: absolute;
}

/** Utility Classes */

.caution-yellow {
  --icon-button-color: var(--jcWarning);

  color: var(--jcWarning);
}

.error-red {
  --icon-button-color: var(--jcError);

  color: var(--jcError);
}

.inactive-grey {
  --icon-button-color: var(--jcGrey400);

  color: var(--jcGrey400);
}

.success-green {
  --icon-button-color: var(--jcSuccess);

  color: var(--jcSuccess);
}

.margin-right {
  margin-right: var(--jc-spacer-small);
}

.red-error__text {
  color: var(--jcError);
}

.vertical-align {
  top: 50%;
  transform: translateY(-50%);
}

/** Recursively select all direct child elements of ItemWithLabel */
.ItemWithLabel > *:not(.tooltip-icon__wrapper),
.chevron-right__icon {
  pointer-events: none;
}
</style>
